import { useEffect, useMemo, useState } from "react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { DEFINED_PERMISSIONS } from "../../../../../utils/const";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions";
import { useSearchFilter } from "../../../../../utils/functions/useDate";
import {
  useDeleteStatusLabelMutation,
  useGetPerformanceLabelQuery,
} from "../../../../api/hrApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import { CTAButtons } from "../../../../global/components/Buttons/buttons";
import PageStyle from "../../../Components/Layout/PageLayout";
import { TableActions } from "../../../Components/Misc/Actions";
import DeleteItemModal from "../../../Components/Modals/DeleteItemModal";
import { AppModalTemplate } from "../../../Components/Modals/Modals";
import Pagination from "../../../Components/Pagination/Pagination";
import {
  CalendarFilter2,
  ProDropFilter,
  SearchFilter,
} from "../../../Components/Search/Search";
import Table from "../../../Components/Table/Table";
import { getStatusText } from "../../Finance/CostCenter/Class";
import EditStatusLabelModal from "./Modals/EditStatusLabelModal";
import StatusLabelModal from "./Modals/StatusLabelModal";
import { FilterByStatus, FilterObjectiveByStatus } from "./status";

const StatusLabel = () => {
  // Helper function to convert color names to RGB values
  const getColorRgb = (colorName) => {
    const colorMap = {
      red: "239, 68, 68",
      blue: "59, 130, 246",
      green: "34, 197, 94",
      yellow: "234, 179, 8",
      white: "255, 255, 255",
      purple: "168, 85, 247",
      orange: "249, 115, 22",
      pink: "236, 72, 153",
      gray: "107, 114, 128",
      black: "0, 0, 0",
    };
    return colorMap[colorName?.toLowerCase()] || "107, 114, 128"; // fallback to gray
  };

  const [onDelete] = useDeleteStatusLabelMutation();
  const [openModal, setOpenModal] = useState(false);
  const [openCreatModal, setOpenCreatModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [matchedPermissionHr, setMatchedPermissionHr] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    const userPerm = JSON.parse(localStorage.getItem("x-u-perm"));
    const hrPerm = [
      DEFINED_PERMISSIONS.SeniorHRManager,
      DEFINED_PERMISSIONS.PrimaryHRManager,
    ];

    // Check if userPerm exists and matches any permission in hrPerm
    if (userPerm && userPerm.some((perm) => hrPerm.includes(perm))) {
      const matchedPerm = userPerm.find((perm) => hrPerm.includes(perm));
      setMatchedPermissionHr(matchedPerm); // Set matchedPermissionHr to the matched permission
    } else {
      setMatchedPermissionHr(""); // Reset to empty string if not matched
    }
  }, []);
  const navigate = useNavigate();
  const handleModal = () => {
    setOpenModal(!openModal);
  };
  const handleOpenCreateModale = () => {
    setOpenCreatModal(!openCreatModal);
  };
  const {
    searchText,
    setSearchText,
    pageSize,
    setPageSize,
    setCurrentPage,
    currentPage,
    filter,
    setFilter,
  } = useSearchFilter({
    initialFilter: 1,
  });
  const { data, isLoading } = useGetPerformanceLabelQuery({
    GeneralSearch: searchText,
    PageNumber: currentPage,
    PageSize: pageSize,
    StartDate: startDate,
    EndDate: endDate,
    ApprovalStatus: filter,
  });
  const actionButton = (
    <>
      <CTAButtons onClick={handleOpenCreateModale}>
        Create Perfomance Status Label
      </CTAButtons>
    </>
  );
  const handleOpenDeleteModal = (id) => {
    if (id) {
      setSelectedId(id);
      setOpenDelete(true);
    }
  };
  const handleCloseDelete = () => {
    setSelectedId(null);
    setOpenDelete(false);
  };
  const handleDelete = async () => {
    try {
      const response = await onDelete({
        id: parseInt(selectedId),
      }).unwrap();
      if (response?.successful) {
        handleCloseDelete();
        toast.success(response?.statusMessage);
      } else {
        toast.error(response?.data?.title);
      }
    } catch (error) {
      toast.error(error.message ?? "Error deleting rating scale");
    }
  };
  const handleView = (id) => {
    if (id) {
      navigate(`${id}/view`);
    }
  };
  const handleEdit = (id) => {
    if (id) {
      setSelectedId(id);
      handleModal();
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!data?.result) return [];
    return [...data.result].sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      if (sortColumn === "fromScore" || sortColumn === "toScore") {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (sortColumn === "createdDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data?.result, sortColumn, sortDirection]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  return (
    <PageStyle
      action={actionButton}
      hasBack={true}
      backURL={"/dashboard/hr/HR-Appraisal-Setup"}
      hasTopPageDemo
      support={
        "Helps the system suggest appropriate actions based on performances score"
      }
      demoTitle={"PERFORMANCE STATUS LABEL"}
    >
      <form className={DashboardStyle.dashboard_filter}>
        <SearchFilter
          value={searchText}
          text={(searchText) => setSearchText(searchText)}
        />
        <CalendarFilter2
          name="Select Date Range"
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          normalDate
        />
        <ProDropFilter
          filter={filter}
          setFilter={setFilter}
          name={"Status"}
          filterBy={FilterByStatus}
        />
      </form>
      <div className={DashboardStyle.dashboard_table_holder}>
        <Table>
          <thead>
            <tr>
              <th>S/N</th>
              <th
                onClick={() => handleSort("performanceStatusLabelName")}
                style={{ cursor: "pointer" }}
              >
                Title {renderSortIcon("performanceStatusLabelName")}
              </th>
              <th
                onClick={() => handleSort("fromScore")}
                style={{ cursor: "pointer" }}
              >
                From {renderSortIcon("fromScore")}
              </th>
              <th
                onClick={() => handleSort("toScore")}
                style={{ cursor: "pointer" }}
              >
                To {renderSortIcon("toScore")}
              </th>
              <th>Color</th>
              <th
                onClick={() => handleSort("createdBy")}
                style={{ cursor: "pointer" }}
              >
                Initiator's Name {renderSortIcon("createdBy")}
              </th>
              <th
                onClick={() => handleSort("createdDate")}
                style={{ cursor: "pointer" }}
              >
                Created Date {renderSortIcon("createdDate")}
              </th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : sortedData?.length > 0 ? (
              sortedData?.map((item, index) => {
                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.performanceStatusLabelName}</td>
                    <td>{item.fromScore}</td>
                    <td>{item.toScore}</td>
                    <td>
                      <div
                        style={{
                          backgroundColor: `rgba(${getColorRgb(
                            item.colorType
                          )}, ${item.colorType === "white" ? "" : "0.5"})`,
                          color:
                            item.colorType?.toLowerCase() === "white"
                              ? "#000000"
                              : `rgb(${getColorRgb(item.colorType)})`,
                        }}
                        className="p-2 rounded"
                      >
                        {item.colorType}
                      </div>
                    </td>
                    <td>{item?.createdBy}</td>
                    <td>{FormatDateTime(item.createdDate, "DD/MM/YYYY")}</td>
                    <td>
                      {typeof item?.approvalStatus === "number"
                        ? getStatusText(item?.approvalStatus)
                        : item?.approvalStatus}
                    </td>
                    <td>
                      <TableActions
                        hasChildren={true}
                        url={matchedPermissionHr ? `${item.id}/view` : null}
                      >
                        {[
                          {
                            name: item?.approvalStatus !== 0 ? "Edit" : null,
                            action:
                              item?.approvalStatus !== 0
                                ? () => handleEdit(item.id)
                                : null,
                          },
                          {
                            name: matchedPermissionHr ? "View" : null,
                            action: () =>
                              matchedPermissionHr ? handleView(item.id) : null,
                          },
                          {
                            name: "Delete",
                            action: () => handleOpenDeleteModal(item.id),
                          },
                        ]}
                      </TableActions>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="text-center">
                  No Status Label Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Pagination
        last_page={data?.totalPages}
        present_page={data?.currentPage}
        totalRows={data?.totalRows}
        pageSize={pageSize}
        setPageSize={(page) => setPageSize(page)}
        click={(page) => setCurrentPage(page)}
      />
      {openModal && selectedId && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openModal}
          setIsOpen={handleModal}
        >
          <EditStatusLabelModal
            isOpen={openModal}
            setIsOpen={setOpenModal}
            onValidate={handleModal}
            id={selectedId}
          />
        </AppModalTemplate>
      )}
      {openCreatModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openCreatModal}
          setIsOpen={handleOpenCreateModale}
        >
          <StatusLabelModal
            isOpen={openCreatModal}
            setIsOpen={setOpenCreatModal}
            onValidate={handleOpenCreateModale}
          />
        </AppModalTemplate>
      )}
      <DeleteItemModal
        isOpen={openDelete}
        handleClose={handleCloseDelete}
        handleDelete={handleDelete}
        headText={"Delete Performance Status Label"}
        infoText={
          "Are you sure you want to Delete this Performance Status Label?"
        }
      />
    </PageStyle>
  );
};

export default StatusLabel;
